// Design language F pieces shared by /signup, /login, /forgotpassword and
// SignInModal. Presentational only: every auth call stays in the page or
// modal that owns it.
import React from "react";
import Link from "next/link";
import { FcGoogle } from "react-icons/fc";
import { Spinner } from "@jammable/ui-components/Loader";
import { useT } from "@jammable/ui-core/src/i18n";

export const authLabel =
  "tw-flex tw-flex-col tw-gap-1.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink";

export const authInput =
  "tw-h-[52px] tw-w-full tw-rounded-full tw-border-2 tw-border-solid tw-border-transparent tw-bg-jam-lav tw-px-5 tw-text-[15px] tw-font-bold tw-text-jam-ink tw-outline-none tw-transition-colors placeholder:tw-font-semibold placeholder:tw-text-jam-muted/70 focus:tw-border-jam-soft";

export const authInputError = "!tw-border-[#E8A3B5]";

export const authFieldError = "tw-text-xs tw-font-bold tw-text-[#B4234A]";

export const authPrimary =
  "tw-flex tw-h-14 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-base tw-font-extrabold tw-text-white tw-transition-opacity hover:tw-opacity-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-60";

export const authLink =
  "tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple";

export const GoogleButton = ({ onClick, loading = false, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="tw-flex tw-h-14 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2.5 tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-text-base tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-border-jam-purple disabled:tw-opacity-60"
  >
    {loading ? (
      <Spinner size={4} className="tw-border-3" />
    ) : (
      <>
        <FcGoogle size={22} />
        {children}
      </>
    )}
  </button>
);

export const OrDivider = ({ label }) => {
  const t = useT();
  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-text-xs tw-font-extrabold tw-text-jam-muted">
      <span className="tw-h-0.5 tw-flex-grow tw-bg-jam-lav" />
      <span className="tw-shrink-0 tw-whitespace-nowrap">
        {label ?? t("auth.orEmail")}
      </span>
      <span className="tw-h-0.5 tw-flex-grow tw-bg-jam-lav" />
    </div>
  );
};

export const AuthError = ({ children }) => (
  <div className="tw-rounded-[22px] tw-bg-[#FDECF1] tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-font-extrabold tw-text-[#B4234A]">
    {children}
  </div>
);

// A Firebase error in the visitor's language. `messages` is the English map
// from utils/tools.js (customSignInErrorMessages etc.) and `scope` its
// translations in messages/auth.ts (auth.error.<scope>.<code>). Codes the map
// doesn't know keep Firebase's own message, as before.
export const authErrorText = (t, scope, messages, error) => {
  const english = messages[error?.code];
  if (!english) return error?.message;
  const key = `auth.error.${scope}.${String(error.code).replace("auth/", "")}`;
  const text = t(key);
  return text === key ? english : text;
};

// Sign up / Log in segmented control. Links, so each page keeps its own URL.
export const AuthTabs = ({ active }) => {
  const t = useT();
  const tab = (href, label, isActive) => (
    <Link
      href={href}
      aria-current={isActive ? "page" : undefined}
      className={`tw-flex-grow tw-rounded-full tw-p-2.5 tw-text-center tw-no-underline ${
        isActive
          ? "tw-bg-white tw-text-jam-purple"
          : "tw-text-jam-ink hover:tw-text-jam-purple"
      }`}
    >
      {label}
    </Link>
  );
  return (
    <div className="tw-flex tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-p-1 tw-text-sm tw-font-extrabold md:tw-p-[5px]">
      {tab("/signup", t("auth.signUp"), active === "signup")}
      {tab("/login", t("auth.logIn"), active === "login")}
    </div>
  );
};

// Full-page auth: lavender hero card on the left (a banner on phones), form
// column on the right.
export const AuthLayout = ({ heading, blurb, children }) => (
  <div className="tw-grid tw-w-full tw-bg-white tw-text-jam-ink md:tw-min-h-[calc(100vh-80px)] md:tw-grid-cols-2">
    <section className="tw-relative tw-flex tw-min-h-[200px] tw-flex-col tw-justify-end tw-overflow-hidden tw-rounded-b-[40px] tw-bg-jam-lav tw-p-6 md:tw-m-4 md:tw-min-h-0 md:tw-rounded-[44px] md:tw-p-12">
      <span
        aria-hidden
        className="tw-absolute tw-right-[30px] tw-top-6 tw-h-[70px] tw-w-[70px] tw-rounded-full tw-border-[5px] tw-border-solid tw-border-white tw-bg-[#F7D84A] md:tw-right-[70px] md:tw-top-[120px] md:tw-h-[110px] md:tw-w-[110px] md:tw-border-[6px]"
      />
      <span
        aria-hidden
        className="tw-absolute tw-right-[110px] tw-top-[74px] tw-h-[50px] tw-w-[50px] tw-rounded-full tw-border-4 tw-border-solid tw-border-white tw-bg-[#F5A25D] md:tw-right-[200px] md:tw-top-[240px] md:tw-h-20 md:tw-w-20 md:tw-border-[6px]"
      />
      <span
        aria-hidden
        className="tw-absolute tw-right-[90px] tw-top-[310px] tw-hidden tw-h-[92px] tw-w-[92px] tw-rounded-full tw-border-[6px] tw-border-solid tw-border-white tw-bg-[#7FD3D8] md:tw-block"
      />
      <div className="tw-relative tw-flex tw-max-w-[460px] tw-flex-col tw-gap-3.5">
        <h1 className="jam-display tw-m-0 tw-text-[38px] tw-font-extrabold tw-leading-none md:tw-text-[60px]">
          {heading}
        </h1>
        {blurb && (
          <p className="tw-m-0 tw-hidden tw-text-[17px] tw-font-semibold tw-text-jam-muted md:tw-block">
            {blurb}
          </p>
        )}
      </div>
    </section>
    <section className="tw-flex tw-items-center tw-justify-center tw-px-5 tw-pb-12 tw-pt-6 md:tw-p-8">
      <div className="tw-flex tw-w-full tw-max-w-[420px] tw-flex-col tw-gap-4">
        {children}
      </div>
    </section>
  </div>
);
